import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Icon,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import {getReferenciaTemporalOcorrencia} from "../../shared/ocorrenciaTemporalUtil";
import {getLabelEstado} from "../../shared/estados";
import IconBadge from "../icon-badge/IconBadge";

const ESTADO_SUCESSO = "PROCESSADO_COM_SUCESSO";

const ROTULOS_COLECOES = {
    componentesFolhaPagamento: "Componentes da folha",
    contasBancarias: "Contas bancárias",
    encerramentoContasBancarias: "Encerramentos",
    servidores: "Servidores",
    folhaPagamento: "Servidores",
    dados: "Registros",
    componentesFiscais: "Componentes fiscais",
    gestoesFiscais: "Registros fiscais"
};

const ROTULOS_QUANTIDADES = {
    quantidadeComponenteFolha: "Componentes da folha",
    quantidadeContaBancaria: "Contas bancárias",
    quantidadeEncerramentoContaBancaria: "Encerramentos",
    quantidadeRegistros: "Registros"
};

export function ResumoProcessamento({ocorrencia, ocorrenciaRaiz}) {
    const resumo = montarResumoProcessamento(ocorrencia, ocorrenciaRaiz);

    if (!resumo.metricas.length && !resumo.metadados.length) {
        return null;
    }

    return (
        <Card variant="outlined" sx={{borderRadius: 2}}>
            <CardContent>
                <Stack
                    direction={{xs: "column", sm: "row"}}
                    justifyContent="space-between"
                    alignItems={{sm: "center"}}
                    spacing={1}
                    mb={2}
                >
                    <Box>
                        <Typography variant="h6">
                            Resumo do processamento
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Indicadores consolidados para conferência rápida do
                            evento e de suas etapas vinculadas.
                        </Typography>
                    </Box>
                </Stack>

                {resumo.metadados.length > 0 && (
                    <Stack
                        direction="row"
                        flexWrap="wrap"
                        mb={2}
                        sx={{gap: 1}}
                        aria-label="Contexto do processamento"
                    >
                        {resumo.metadados.map((metadado) => (
                            <Chip
                                key={metadado.chave}
                                size="small"
                                variant="outlined"
                                color={metadado.cor || "default"}
                                icon={<Icon>{metadado.icone}</Icon>}
                                label={`${metadado.rotulo}: ${metadado.valor}`}
                            />
                        ))}
                    </Stack>
                )}

                {resumo.metricas.length > 0 && (
                    <Grid container spacing={1.5}>
                        {resumo.metricas.map((metrica) => (
                            <Metrica key={metrica.chave} {...metrica} />
                        ))}
                    </Grid>
                )}

                {resumo.exibeAvisoLotesInternos && (
                    <Alert severity="info" sx={{mt: 2}}>
                        Os lotes apresentados nesta tela são partições internas
                        do conector. O envio parcial de Atos de Pessoal é
                        síncrono e não gera lote assíncrono consultável em
                        Serviços Gerais.
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
}

function Metrica({icone, rotulo, valor, detalhe, cor = "primary"}) {
    return (
        <Grid item xs={12} sm={6} lg={4}>
            <Box
                sx={{
                    height: "100%",
                    minHeight: 116,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.paper"
                }}
            >
                <IconBadge color={cor}>{icone}</IconBadge>
                <Typography variant="caption" color="text.secondary">
                    {rotulo}
                </Typography>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                        overflowWrap: "anywhere",
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1.2
                    }}
                >
                    {valor}
                </Typography>
                {detalhe && (
                    <Typography variant="caption" color="text.secondary">
                        {detalhe}
                    </Typography>
                )}
            </Box>
        </Grid>
    );
}

export function EtapasFluxo({
    ocorrencia,
    ocorrenciaRaiz,
    onNavigate,
    embedded = false
}) {
    const raiz = ocorrenciaRaiz || ocorrencia;
    const etapas = montarEtapasFluxo(raiz);

    if (etapas.length <= 1) {
        return null;
    }

    const concluidas = etapas.filter(
        (etapa) => etapa.estadoEnvio === ESTADO_SUCESSO
    ).length;

    return (
        <Card
            variant={embedded ? "elevation" : "outlined"}
            elevation={0}
            sx={{
                borderRadius: embedded ? 0 : 2,
                border: embedded ? 0 : undefined,
                boxShadow: "none"
            }}
        >
            <CardContent
                sx={embedded ? {p: 0, "&:last-child": {pb: 0}} : undefined}
            >
                <Stack
                    direction={{xs: "column", sm: "row"}}
                    justifyContent="space-between"
                    alignItems={{sm: "center"}}
                    spacing={1}
                    mb={2}
                >
                    <Box>
                        <Typography variant="h6">Etapas do fluxo</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Abertura, partições internas e finalização em uma
                            única linha de acompanhamento.
                        </Typography>
                    </Box>
                    <Chip
                        color={
                            concluidas === etapas.length ? "success" : "default"
                        }
                        label={`${concluidas} de ${etapas.length} concluídas`}
                    />
                </Stack>

                <Stack spacing={1}>
                    {etapas.map((etapa, indice) => {
                        const atual = etapa.id === ocorrencia.id;
                        return (
                            <Button
                                key={etapa.id}
                                variant={atual ? "outlined" : "text"}
                                color={atual ? "primary" : "inherit"}
                                onClick={() => onNavigate?.(etapa.id)}
                                sx={{
                                    justifyContent: "flex-start",
                                    textTransform: "none",
                                    border: 1,
                                    borderColor: atual
                                        ? "primary.main"
                                        : "divider",
                                    bgcolor: atual
                                        ? "action.selected"
                                        : "transparent",
                                    p: 1.5
                                }}
                                startIcon={
                                    <IconBadge
                                        size={32}
                                        color={corIconeEstado(
                                            etapa.estadoEnvio
                                        )}
                                    >
                                        {iconeEstado(etapa.estadoEnvio)}
                                    </IconBadge>
                                }
                            >
                                <Box textAlign="left" flex={1} minWidth={0}>
                                    <Typography fontWeight={700}>
                                        {indice + 1}.{" "}
                                        {rotuloEtapa(etapa.eventoAtual)}
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        mt={0.5}
                                        sx={{gap: 1}}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Ocorrência #{etapa.id}
                                        </Typography>
                                        {etapa.numeroLote != null && (
                                            <Tooltip title="Partição interna do conector; não representa lote assíncrono do e-Sfinge.">
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`Lote interno ${etapa.numeroLote}`}
                                                />
                                            </Tooltip>
                                        )}
                                        {quantidadeEtapa(etapa) != null && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`${quantidadeEtapa(
                                                    etapa
                                                )} registro${
                                                    quantidadeEtapa(etapa) === 1
                                                        ? ""
                                                        : "s"
                                                }`}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                                <Chip
                                    size="small"
                                    color={corChipEstado(etapa.estadoEnvio)}
                                    label={formatarEstado(etapa.estadoEnvio)}
                                />
                            </Button>
                        );
                    })}
                </Stack>
            </CardContent>
        </Card>
    );
}

export function montarResumoProcessamento(ocorrencia = {}, ocorrenciaRaiz) {
    const raiz = ocorrenciaRaiz || ocorrencia;
    const payload = raiz?.dadosEnviados || ocorrencia?.dadosEnviados || {};
    const etapas = montarEtapasFluxo(raiz);
    const evento = `${raiz?.eventoAtual || ocorrencia?.eventoAtual || ""}`;
    const metricas = [];
    const metadados = [];
    const chaves = new Set();
    const chavesMetadados = new Set();

    const adicionar = (metrica) => {
        if (
            metrica?.valor == null ||
            metrica.valor === "" ||
            chaves.has(metrica.chave)
        ) {
            return;
        }
        chaves.add(metrica.chave);
        metricas.push(metrica);
    };

    const adicionarMetadado = (metadado) => {
        if (
            metadado?.valor == null ||
            metadado.valor === "" ||
            chavesMetadados.has(metadado.chave)
        ) {
            return;
        }
        chavesMetadados.add(metadado.chave);
        metadados.push(metadado);
    };

    getReferenciaTemporalOcorrencia(ocorrencia, raiz).itens.forEach(
        adicionarMetadado
    );

    const eFolhaPagamento =
        evento.includes("FOLHA_PAGAMENTO") &&
        !evento.includes("COMPONENTE_FOLHA_PAGAMENTO");

    if (eFolhaPagamento) {
        resumoFolha(payload).forEach(adicionar);
    } else {
        metricasPayloadGenerico(payload).forEach(adicionar);
    }

    const lotes = etapas.filter((etapa) => etapa.numeroLote != null);
    if (lotes.length) {
        adicionarMetadado({
            chave: "lotes-internos",
            icone: "view_list",
            rotulo: lotes.length === 1 ? "Lote interno" : "Lotes internos",
            valor: lotes.length
        });
    }

    const registrosProcessados = lotes.reduce(
        (total, etapa) => total + (quantidadeEtapa(etapa) || 0),
        0
    );
    if (registrosProcessados > 0) {
        adicionar({
            chave: "registros-processados",
            icone: "task_alt",
            rotulo: "Registros processados",
            valor: registrosProcessados
        });
    }

    if (etapas.length > 1) {
        const concluidas = etapas.filter(
            (etapa) => etapa.estadoEnvio === ESTADO_SUCESSO
        ).length;
        adicionarMetadado({
            chave: "etapas",
            icone: "account_tree",
            rotulo: "Etapas",
            valor: `${concluidas}/${etapas.length}`,
            cor: concluidas === etapas.length ? "success" : "default"
        });
    }

    const remessa = ocorrencia?.idRemessa || raiz?.idRemessa;
    if (remessa != null) {
        adicionarMetadado({
            chave: "remessa",
            icone: "receipt_long",
            rotulo: "Remessa e-Sfinge",
            valor: remessa
        });
    }

    return {
        metricas,
        metadados,
        eFluxo: etapas.length > 1,
        exibeAvisoLotesInternos:
            lotes.length > 0 && `${raiz?.contexto || ""}` === "ATOS_DE_PESSOAL"
    };
}

export function montarEtapasFluxo(raiz = {}) {
    if (!raiz?.id) return [];

    return [raiz, ...(raiz.ocorrenciasFilhas || [])].sort((a, b) => {
        const dataA = a.dataHora ? new Date(a.dataHora).getTime() : 0;
        const dataB = b.dataHora ? new Date(b.dataHora).getTime() : 0;
        return dataA - dataB || Number(a.id) - Number(b.id);
    });
}

function resumoFolha(payload) {
    const servidores = primeiraLista(payload, [
        "servidores",
        "folhaPagamento",
        "dados"
    ]);
    const lancamentos = servidores.flatMap((servidor) =>
        Array.isArray(servidor?.lancamentos) ? servidor.lancamentos : []
    );
    const resumoExplicito =
        payload?.resumoFinalizacao || payload?.resumo || payload?.finalizacao;

    const proventos = lancamentos.filter(
        (item) =>
            `${item?.indicativoProventoDesconto || ""}`.toUpperCase() === "P"
    );
    const descontos = lancamentos.filter(
        (item) =>
            `${item?.indicativoProventoDesconto || ""}`.toUpperCase() === "D"
    );

    const quantidadeProventos =
        numero(resumoExplicito?.quantidadeRegistrosProventos) ??
        proventos.length;
    const quantidadeDescontos =
        numero(resumoExplicito?.quantidadeRegistrosDescontos) ??
        descontos.length;
    const quantidadeLancamentos =
        numero(resumoExplicito?.quantidadeRegistrosEnviados) ??
        lancamentos.length;
    const totalProventos =
        numero(resumoExplicito?.somatorioProventos) ??
        somarLancamentos(proventos);
    const totalDescontos =
        numero(resumoExplicito?.somatorioDescontos) ??
        somarLancamentos(descontos);

    return [
        servidores.length > 0 && {
            chave: "servidores",
            icone: "groups",
            rotulo: "Servidores",
            valor: servidores.length
        },
        quantidadeLancamentos > 0 && {
            chave: "lancamentos",
            icone: "format_list_numbered",
            rotulo: "Lançamentos",
            valor: quantidadeLancamentos
        },
        {
            chave: "proventos",
            icone: "trending_up",
            rotulo: "Total de proventos",
            valor: formatarMoeda(totalProventos),
            detalhe: `${quantidadeProventos} lançamento${
                quantidadeProventos === 1 ? "" : "s"
            }`,
            cor: "success"
        },
        {
            chave: "descontos",
            icone: "trending_down",
            rotulo: "Total de descontos",
            valor: formatarMoeda(totalDescontos),
            detalhe: `${quantidadeDescontos} lançamento${
                quantidadeDescontos === 1 ? "" : "s"
            }`,
            cor: "warning"
        },
        {
            chave: "liquido",
            icone: "payments",
            rotulo: "Resultado líquido",
            valor: formatarMoeda(totalProventos - totalDescontos),
            detalhe: "Proventos menos descontos"
        }
    ].filter(Boolean);
}

function metricasPayloadGenerico(payload) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        return Array.isArray(payload)
            ? [
                  {
                      chave: "registros",
                      icone: "format_list_numbered",
                      rotulo: "Registros preparados",
                      valor: payload.length
                  }
              ]
            : [];
    }

    const metricas = [];
    const rotulosJaUsados = new Set();

    Object.entries(ROTULOS_QUANTIDADES).forEach(([chave, rotulo]) => {
        const valor = numero(payload[chave]);
        if (valor != null) {
            rotulosJaUsados.add(rotulo);
            metricas.push({
                chave,
                icone: iconeColecao(chave),
                rotulo,
                valor
            });
        }
    });

    Object.entries(ROTULOS_COLECOES).forEach(([chave, rotulo]) => {
        if (Array.isArray(payload[chave]) && !rotulosJaUsados.has(rotulo)) {
            metricas.push({
                chave,
                icone: iconeColecao(chave),
                rotulo,
                valor: payload[chave].length
            });
        }
    });

    if (!metricas.length) {
        const primeiraColecao = Object.entries(payload).find(([, valor]) =>
            Array.isArray(valor)
        );
        if (primeiraColecao) {
            metricas.push({
                chave: primeiraColecao[0],
                icone: "format_list_numbered",
                rotulo: "Registros preparados",
                valor: primeiraColecao[1].length
            });
        }
    }

    return metricas;
}

function primeiraLista(payload, campos) {
    for (const campo of campos) {
        if (Array.isArray(payload?.[campo])) return payload[campo];
    }
    return [];
}

function somarLancamentos(lancamentos) {
    return lancamentos.reduce(
        (total, item) => total + (numero(item?.valorComponente) || 0),
        0
    );
}

function numero(valor) {
    if (typeof valor === "number" && Number.isFinite(valor)) return valor;
    if (typeof valor !== "string" || !valor.trim()) return null;

    const texto = valor.trim();
    const normalizado = texto.includes(",")
        ? texto.replace(/\./g, "").replace(",", ".")
        : texto;
    const convertido = Number(normalizado);
    return Number.isFinite(convertido) ? convertido : null;
}

function formatarMoeda(valor) {
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL"
    }).format(valor || 0);
}

function quantidadeEtapa(etapa) {
    return etapa?.registrosProcessados ?? etapa?.qtdeRegistros ?? null;
}

function rotuloEtapa(evento) {
    const valor = `${evento || ""}`;
    if (valor.endsWith("_INICIAR")) return "Abertura do pacote";
    if (valor.includes("_ENVIAR_PARCIAL")) return "Envio parcial";
    if (valor.endsWith("_FINALIZAR")) return "Finalização do pacote";
    return valor.replaceAll("_", " ");
}

function formatarEstado(estado) {
    return getLabelEstado(estado) || "Não informado";
}

function iconeEstado(estado) {
    if (estado === ESTADO_SUCESSO) return "check_circle";
    if (estado === "PROCESSADO_COM_ERRO") return "error";
    if (estado === "RESULTADO_INDETERMINADO") return "help";
    if (estado === "PROCESSAMENTO") return "sync";
    return "schedule";
}

function corIconeEstado(estado) {
    if (estado === ESTADO_SUCESSO) return "success";
    if (estado === "PROCESSADO_COM_ERRO") return "error";
    if (estado === "RESULTADO_INDETERMINADO") return "warning";
    if (estado === "PROCESSAMENTO") return "warning";
    if (estado === "AGUARDANDO_APROVACAO") return "info";
    return "primary";
}

function corChipEstado(estado) {
    if (estado === ESTADO_SUCESSO) return "success";
    if (estado === "PROCESSADO_COM_ERRO") return "error";
    if (estado === "RESULTADO_INDETERMINADO") return "warning";
    if (estado === "PROCESSAMENTO") return "info";
    return "default";
}

function iconeColecao(chave) {
    if (chave.toLowerCase().includes("conta")) return "account_balance";
    if (chave.toLowerCase().includes("servidor")) return "groups";
    if (chave.toLowerCase().includes("componente")) return "view_list";
    return "format_list_numbered";
}
